import { FC, useEffect } from "react";
import { FieldFormV2, FieldV2, LoadingV2, CoreFieldValue, NumericFieldProps } from "@ucl/library";
import { useFinancialApplicationForm } from "@/hooks/useFinancialApplicationForm";
import { FIELD_KEYS } from "@/constants";
import "./OrganizationInfoForm.scss";
import { FinancialApplication } from "@/types/FinancialApplication";
import {
  useFinancialApplicationFormSectionValidation,
  FinancialApplicationFormValidationDisplayBehavior,
  FinancialApplicationFormSectionContextType,
} from "@/hooks/useFinancialApplicationFormSectionValidation";
import { FinancialApplicationSection } from "@/types/enums";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";

export interface OrganizationInfoFormProps {
  financialApplicationId: string;
  autoValidationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  availableFields?: string[];
  showHeader?: boolean;
  onBlur?: (field: CoreFieldValue) => void;
  validationContextType: FinancialApplicationFormSectionContextType;
}

export const OrganizationInfoForm: FC<OrganizationInfoFormProps> = (props) => {
  const { refreshCoreData } = useFinancialApplicationCoreData(props.financialApplicationId);
  const {
    formValue,
    setFormValue,
    saveFormData,
    schemaFactory,
    formBuilder,
    fieldFormModule,
    enqueueAfterNextSave,
  } = useFinancialApplicationForm(props.financialApplicationId, props.validationContextType);

  const { validationErrors } = useFinancialApplicationFormSectionValidation(
    props.financialApplicationId,
    FinancialApplicationSection.OrganizationInformation,
    props.autoValidationBehavior,
    props.validationContextType
  );

  // Refetch core data when these fields change to update conditional flags
  useEffect(() => {
    if (formValue) {
      enqueueAfterNextSave(async () => {
        await refreshCoreData();
      });
    }
  }, [
    formValue?.organizationInformation?.naicsCode,
    formValue?.organizationInformation?.organizationType,
  ]);

  if (!schemaFactory || !formBuilder || !formValue) {
    return (
      <section className="organization-information-form">
        <LoadingV2 />
      </section>
    );
  }

  return (
    <div className="organization-information-form">
      <FieldFormV2<FinancialApplication>
        ref={fieldFormModule}
        value={formValue}
        fieldSchemaFormBuilder={formBuilder}
        onFormSubmit={async (value) => {
          await saveFormData(value);
        }}
        onFieldChanged={(newValue, newFieldValue) => {
          setFormValue(newValue, newFieldValue);
        }}
        errors={validationErrors}
        availableFields={props.availableFields}
      >
        <FieldV2
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_INFORMATION}
          complexFieldValue={{
            showHeader: props.showHeader,
            headerLabel: "Basic Information",
            isSoleProprietorship: formValue.organizationInformation?.organizationType === 6,
          }}
        />

        {props.validationContextType === "banker" && (
          <FieldV2
            fieldProps={
              {
                fieldKey: FIELD_KEYS.ORGANIZATION_INFO.FICO_SBSS_SCORE,
                showIncrementButtons: false,
                format: "n0",
              } as NumericFieldProps
            }
          />
        )}

        <FieldV2
          fieldProps={FIELD_KEYS.FINANCIAL_APPLICATION.ORGANIZATION_CONTACT_INFORMATION}
          complexFieldValue={{
            showHeader: props.showHeader,
            headerLabel: "Contact Information",
          }}
        />
      </FieldFormV2>
    </div>
  );
};
